// Return an array of selected timeslots with a quantity of how many of that timeslot have been selected

import { some } from '@utils/some';
import _ from 'lodash';
import moment from 'moment';
import {
  ConfigurationGroupOptionType,
  SpaPackageAvailabilityFormType,
  TimeSlotType,
} from 'shared-types/SpaPackage';

function getTimeslotKey(id1, id2, startTime) {
  return `${id1}_${id2}_${startTime}`;
}
function getTimeslotKeyByStaffAndRoom(
  configGroupId: number,
  optionId: number,
  startTime: string,
  endTime: string,
  guestIndex: number,
  staffId?: string,
  roomId?: string
) {
  return `${configGroupId}_${optionId}_${startTime}_${endTime}_${guestIndex}_${staffId}_${roomId}`;
}

export const generateSelected = (inputFields) => {
  if (!inputFields) {
    return null;
  }
  return inputFields
    .flatMap((field) => {
      if (!field || !field.configurationGroups) {
        return null;
      }
      return Object.keys(field.configurationGroups).flatMap((group) =>
        field.configurationGroups[group].flatMap(
          (item) =>
            item.timeslot &&
            getTimeslotKey(item.configurationGroupId, item.optionId, item.timeslot.startTime)
        )
      );
    })
    .filter((result) => result)
    .reduce(
      (prev, cur) => (prev[cur] ? { ...prev, [cur]: prev[cur] + 1 } : { ...prev, [cur]: 1 }),
      {}
    );
};

export const generateSelectedByStaffAndRooms = (inputFields: SpaPackageAvailabilityFormType) => {
  if (!inputFields) {
    return null;
  }
  const allocatedStaffMap = new Map<string, string[]>();
  const allocatedRoomsMap = new Map<string, string[]>();
  return inputFields
    .flatMap((field, guestIndex) => {
      if (!field || !field.configurationGroups) {
        return null;
      }

      return Object.keys(field.configurationGroups).flatMap((group) =>
        field.configurationGroups[group].flatMap((item) => {
          if (item.timeslot) {
            const unavailableStaffForTimeslot = allocatedStaffMap.get(item.timeslot.startTime);
            const unavailableRoomsForTimeslot = allocatedRoomsMap.get(item.timeslot.startTime);
            //basic logic here for timeslot allocation for now, will just select the first that it finds
            const staffId = item.timeslot.staff?.find(
              (s) => !unavailableStaffForTimeslot?.includes(s)
            );
            const roomId = item.timeslot.rooms?.find(
              (r) => !unavailableRoomsForTimeslot?.includes(r)
            );
            if (staffId && roomId) {
              allocatedStaffMap.set(item.timeslot.startTime, [
                ...(unavailableStaffForTimeslot || []),
                staffId,
              ]);
              allocatedRoomsMap.set(item.timeslot.startTime, [
                ...(unavailableRoomsForTimeslot || []),
                roomId,
              ]);
            }

            return getTimeslotKeyByStaffAndRoom(
              item.configurationGroupId,
              item.optionId,
              item.timeslot.startTime,
              item.timeslot.endTime,
              guestIndex,
              staffId,
              roomId
            );
          }
        })
      );
    })
    .filter((result) => result)
    .reduce((prev, cur) => {
      if (cur) {
        return prev[cur] ? { ...prev, [cur]: prev[cur] + 1 } : { ...prev, [cur]: 1 };
      }
      return prev;
    }, {});
};

// Check if timeslot overlaps with a slot that has no quantity remaining
const filterBeforeOrAfter = (id, optionId, timeslot, slotsWithNoQuantity: string[]) => {
  let returnValue = true;
  for (let i = 0; i < slotsWithNoQuantity.length; i += 1) {
    const [slotId, slotOptionId, slotStart, slotEnd] = slotsWithNoQuantity[i].split('_');
    if (
      slotId === `${id}` &&
      slotOptionId === `${optionId}` &&
      (moment(timeslot.startTime, 'hh:mm').isBetween(
        moment(slotStart, 'hh:mm'),
        moment(slotEnd, 'hh:mm')
      ) ||
        moment(timeslot.endTime, 'hh:mm').isBetween(
          moment(slotStart, 'hh:mm'),
          moment(slotEnd, 'hh:mm')
        ))
    ) {
      returnValue = false;
      break;
    }
  }
  return returnValue;
};

const timeslotsOverlap = (
  timeslot1: {
    startTime: string;
    endTime: string;
  },
  timeslot2: {
    startTime: string;
    endTime: string;
  }
) =>
  moment(timeslot1.startTime, 'hh:mm').isBetween(
    moment(timeslot2.startTime, 'hh:mm'),
    moment(timeslot2.endTime, 'hh:mm'),
    undefined,
    '[)'
  ) ||
  moment(timeslot1.endTime, 'hh:mm').isBetween(
    moment(timeslot2.startTime, 'hh:mm'),
    moment(timeslot2.endTime, 'hh:mm'),
    undefined,
    '(]'
  );

// Check if timeslot overlaps with a slot that has no quantity remaining
const filterBeforeOrAfterByStaffAndRoomAvailability = (
  configurationGroupId: number,
  optionId: number,
  timeslot: TimeSlotType,
  slotsWithNoQuantity: string[],
  guestIndex: number
) => {
  //treatments
  if (timeslot.staff && timeslot.rooms) {
    return true;
  }

  //facility
  return !some(slotsWithNoQuantity, (slot) => {
    const [configGroupId, slotOptionId, slotStart, slotEnd, gI] = slot.split('_');

    return (
      String(guestIndex) === gI &&
      configGroupId === String(configurationGroupId) &&
      slotOptionId === String(optionId) &&
      timeslotsOverlap(
        { startTime: timeslot.startTime, endTime: timeslot.endTime },
        { startTime: slotStart, endTime: slotEnd }
      )
    );
  });
};

// Filter timeslots, remove a specific timeslot if its quantity matches the number of times it has been selected (i.e. none left)
export const filterTimeslotOptions = (inputOptions, selected, id) => {
  const slotsWithNoQuantity = [] as string[];
  inputOptions.forEach((option) => {
    option.timeslots?.forEach((timeslot) => {
      if (
        selected &&
        selected[getTimeslotKey(id, option.id, timeslot.startTime)] &&
        selected[getTimeslotKey(id, option.id, timeslot.startTime)] >= timeslot.quantity
      ) {
        slotsWithNoQuantity.push(
          `${getTimeslotKey(id, option.id, timeslot.startTime)}_${timeslot.endTime}`
        );
      }
    });
  });

  return inputOptions.map((option) => ({
    ...option,
    timeslots:
      selected && Object.keys(selected).length
        ? option.timeslots?.filter((timeslot) =>
            selected[`${id}_${option.id}_${timeslot.startTime}`]
              ? selected[getTimeslotKey(id, option.id, timeslot.startTime)] < timeslot.quantity
              : filterBeforeOrAfter(id, option.id, timeslot, slotsWithNoQuantity)
          )
        : option.timeslots,
  }));
};

// Filter timeslots, remove availability from a timeslot where the staff member it needs is already assigned to another selection
export const filterTimeslotOptionsByStaffAndRoomAvailability = (
  inputOptions: ConfigurationGroupOptionType[],
  selected: Record<string, number> | null,
  id: number,
  guestIndex: number
) => {
  const slotsWithNoQuantity = [] as string[];
  const copyInputOptions = _.cloneDeep(inputOptions);

  copyInputOptions.forEach((option) => {
    option.timeslots?.forEach((timeslot) => {
      //facilities
      if (!timeslot.staff && !timeslot.rooms) {
        const tsKey = getTimeslotKeyByStaffAndRoom(
          id,
          option.id,
          timeslot.startTime,
          timeslot.endTime,
          guestIndex,
          undefined,
          undefined
        );
        if (
          !timeslot.quantity ||
          (selected && selected[tsKey] && selected[tsKey] >= timeslot.quantity)
        ) {
          slotsWithNoQuantity.push(tsKey);
        }
        return;
      }

      Object.keys(selected ?? {}).forEach((key) => {
        const [cgid, oid, slotStart, slotEnd, gi, staffId, roomId] = key.split('_');
        if (cgid === String(id) && oid === String(option.id) && gi === String(guestIndex)) {
          return;
        }
        const indexStaff = timeslot.staff?.findIndex((s) => s === staffId);
        const indexRoom = timeslot.rooms?.findIndex((r) => r === roomId);
        const overlapsWithTimeslot = timeslotsOverlap(timeslot, {
          startTime: slotStart,
          endTime: slotEnd,
        });

        //remove unavailable staff and rooms from the timeslot
        if (indexStaff !== undefined && indexStaff !== -1 && overlapsWithTimeslot) {
          timeslot.staff?.splice(indexStaff, 1);
        }
        if (indexRoom !== undefined && indexRoom !== -1 && overlapsWithTimeslot) {
          timeslot.rooms?.splice(indexRoom, 1);
        }
      });
    });
  });

  return copyInputOptions.map((option) => ({
    ...option,
    timeslots:
      selected && Object.keys(selected).length
        ? option.timeslots?.filter((timeslot) => {
            if (timeslot.staff?.length === 0 || timeslot.rooms?.length === 0) {
              const availabilityByCurrentSelections = Object.keys(selected).reduce(
                (acc, key) => {
                  const [configId, optionId, slotStart, slotEnd, guestId] = key.split('_');
                  const overlapsWithTimeslot = timeslotsOverlap(timeslot, {
                    startTime: slotStart,
                    endTime: slotEnd,
                  });
                  const sameOptionAndOverlap =
                    String(id) === configId &&
                    String(option.id) === optionId &&
                    overlapsWithTimeslot;

                  const isHiddenByCurrentGuestSelection =
                    String(guestIndex) === guestId && sameOptionAndOverlap;

                  const isHiddenByAnotherGuestSelection =
                    String(guestIndex) !== guestId && sameOptionAndOverlap;

                  if (isHiddenByAnotherGuestSelection) {
                    acc.isHiddenByAnotherGuestSelection = true;
                  }
                  if (isHiddenByCurrentGuestSelection) {
                    acc.isHiddenByCurrentGuestSelection = true;
                  }
                  return acc;
                },
                { isHiddenByAnotherGuestSelection: false, isHiddenByCurrentGuestSelection: false }
              );

              // should never show a timeslot if it is blocked by another guest, but should show it if its only blocked by the current guest
              return availabilityByCurrentSelections.isHiddenByAnotherGuestSelection
                ? false
                : availabilityByCurrentSelections.isHiddenByCurrentGuestSelection;
            } else {
              return filterBeforeOrAfterByStaffAndRoomAvailability(
                id,
                option.id,
                timeslot,
                slotsWithNoQuantity,
                guestIndex
              );
            }
          })
        : option.timeslots,
  }));
};
